import { TFunction } from 'i18next';
import { LmsCurriculumListItem } from './types';
import { DataTableMeta, DateColumn, LimitedContent } from '@otp/ui';
import { ActionsField } from './columns/actions';
import { UserLevelColumn } from 'src/components/common/table/columns/level';
import { CurriculumModeColumn } from './columns/mode';
import { CurriculumStatusColumn } from './columns/status';

export const getTableConfig = (
  curriculums: LmsCurriculumListItem[],
  { isAdmin, t }: { isAdmin: boolean; t: TFunction },
) => {
  const columns = [];

  if (isAdmin) {
    columns.push({
      name: 'changesLevelTo',
      label: t('common.level'),
      options: {
        sort: true,
        customBodyRender: (_: any, column: DataTableMeta) => {
          const curriculum = curriculums[column.rowIndex];

          return (
            <UserLevelColumn level={curriculum.changesLevelTo || undefined} />
          );
        },
      },
    });
  } else {
    columns.push({
      name: 'status',
      label: t('common.status.label'),
      options: {
        customBodyRender: (_: any, column: DataTableMeta) => {
          const curriculum = curriculums[column.rowIndex];
          const userCurriculum = curriculum.curriculumUsers?.[0];

          return (
            <CurriculumStatusColumn
              status={userCurriculum?.status}
              progress={userCurriculum?.progress}
            />
          );
        },
      },
    });
  }

  columns.push({
    name: 'name',
    label: t('common.name'),
    options: {
      sort: true,
    },
  });

  columns.push({
    name: 'description',
    label: t('common.description'),
    options: {
      customBodyRender: (value: string) => (
        <LimitedContent maxRows={2}>
          <div dangerouslySetInnerHTML={{ __html: value }} />
        </LimitedContent>
      ),
    },
  });

  columns.push({
    name: 'estimatedTime',
    label: t('lms.curriculum.list.columns.estimated-time'),
  });

  columns.push({
    name: 'mode',
    label: t('lms.curriculum.list.columns.mode.label'),
    options: {
      sort: true,
      customBodyRender: (_: any, column: DataTableMeta) => {
        const curriculum = curriculums[column.rowIndex];

        return <CurriculumModeColumn mode={curriculum.mode} />;
      },
    },
  });

  if (!isAdmin) {
    columns.push(
      {
        name: 'startedAt',
        label: t('common.started-at'),
        options: {
          customBodyRender: (_: any, column: DataTableMeta) => {
            const curriculum = curriculums[column.rowIndex];
            const userCurriculum = curriculum.curriculumUsers?.[0];

            return userCurriculum?.startedAt ? (
              <DateColumn date={userCurriculum?.startedAt} />
            ) : null;
          },
        },
      },
      {
        name: 'completedAt',
        label: t('common.completed-at'),
        options: {
          customBodyRender: (_: any, column: DataTableMeta) => {
            const curriculum = curriculums[column.rowIndex];
            const userCurriculum = curriculum.curriculumUsers?.[0];

            return userCurriculum?.completedAt ? (
              <DateColumn date={userCurriculum?.completedAt} />
            ) : null;
          },
        },
      },
    );
  }

  columns.push({
    name: 'actions',
    label: t('common.actions'),
    options: {
      customBodyRender: (_: any, column: DataTableMeta) => {
        const curriculum = curriculums[column.rowIndex];

        return <ActionsField curriculum={curriculum} isAdmin={isAdmin} />;
      },
    },
  });

  return columns;
};
